import React from 'react';
import { useState } from 'react';
import { Database } from '../types/supabase'; 
import { Building2, FolderKanban, CheckSquare, TrendingUp, Calendar } from 'lucide-react';

type Agency = Database['public']['Tables']['agencies']['Row'];
type Project = Database['public']['Tables']['projects']['Row'];
type Task = Database['public']['Tables']['tasks']['Row'];

interface DashboardStatsProps {
  agencies: Agency[];
  projects: Project[];
  tasks: Task[];
}

type DueDateFilter = '7' | '14' | '30' | 'today';

export default function DashboardStats({ agencies, projects, tasks }: DashboardStatsProps) {
  const [dueDateFilter, setDueDateFilter] = useState<DueDateFilter>('7');
  const [showAttentionRequired, setShowAttentionRequired] = useState(true);

  // Calculate statistics
  const activeProjects = projects.length;
  const totalAgencies = agencies.length;
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(task => task.completed).length;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // Get overdue tasks
  const overdueTasks = tasks.filter(task => {
    if (!task.due_date || task.completed) return false;
    const dueDate = new Date(task.due_date);
    const now = new Date();
    return dueDate < now;
  }).sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime());

  // Get tasks due within the selected timeframe
  const getTasksDue = (days: number | 'today') => {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of day
    
    const endDate = days === 'today' 
      ? new Date(now.setHours(23, 59, 59, 999))
      : new Date(now.getTime() + (Number(days) * 24 * 60 * 60 * 1000));
    
    return tasks
      .filter(task => {
        if (!task.due_date || task.completed) return false;
        const dueDate = new Date(task.due_date);
        dueDate.setHours(0, 0, 0, 0); // Set to start of day for fair comparison
        return dueDate >= now && dueDate <= endDate;
      })
      .sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime());
  };

  const tasksDue = getTasksDue(dueDateFilter);

  // Calculate top performing customers with proper task association
  const agencyCompletionRates = agencies.map(agency => {
    // Get all tasks associated with this customer directly or through projects
    const agencyTasks = tasks.filter(task => 
      task.agency_id === agency.id || // Direct association
      projects.some(p => 
        p.agency_name === agency.name && // Project association
        p.id === task.project_id
      )
    );
    
    const completedAgencyTasks = agencyTasks.filter(t => t.completed);
    const rate = agencyTasks.length > 0
      ? Math.round((completedAgencyTasks.length / agencyTasks.length) * 100)
      : null;
    
    return {
      name: agency.name,
      completionRate: rate,
      totalTasks: agencyTasks.length,
      completedTasks: completedAgencyTasks.length
    };
  })
  .filter(agency => agency.totalTasks > 0)
  .sort((a, b) => (b.completionRate || 0) - (a.completionRate || 0));

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Building2 className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Customers</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900 dark:text-white">{totalAgencies}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FolderKanban className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Active Projects</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900 dark:text-white">{activeProjects}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckSquare className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Tasks</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900 dark:text-white">{totalTasks}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Completion Rate</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900 dark:text-white">{completionRate}%</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Attention Required Section */}
        {showAttentionRequired && overdueTasks.length > 0 && (
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border-l-4 border-red-500">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg leading-6 font-medium text-red-600 dark:text-red-400">
                  Attention Required
                </h3>
                <button
                  onClick={() => setShowAttentionRequired(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                {overdueTasks.map((task) => {
                  const dueDate = new Date(task.due_date!);
                  const now = new Date();
                  const diffTime = Math.abs(now.getTime() - dueDate.getTime());
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  
                  return (
                    <div key={task.id} className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                          {task.title}
                        </h4>
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                          Overdue by {diffDays} {diffDays === 1 ? 'day' : 'days'}
                        </p>
                      </div>
                      <span className={`ml-4 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        task.priority === 'Urgent'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200'
                          : task.priority === 'High'
                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200'
                          : task.priority === 'Normal'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                      }`}>
                        {task.priority}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Tasks Due */}
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Tasks Due</h3>
              <select
                value={dueDateFilter}
                onChange={(e) => setDueDateFilter(e.target.value as DueDateFilter)}
                className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="today">Due Today</option>
                <option value="7">Next 7 Days</option>
                <option value="14">Next 14 Days</option>
                <option value="30">Next 30 Days</option>
              </select>
            </div>
            <div className="mt-6">
              {tasksDue.length > 0 ? (
                <div className="space-y-4">
                  {tasksDue.map((task) => (
                    <div key={task.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">{task.title}</h4>
                        <div className="flex items-center mt-1">
                          <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Due: {new Date(task.due_date!).toLocaleDateString(undefined, {
                              weekday: 'short',
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <span className={`ml-4 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        task.priority === 'Urgent'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200'
                          : task.priority === 'High'
                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200'
                          : task.priority === 'Normal'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                      }`}>
                        {task.priority}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                  No tasks due {dueDateFilter === 'today' ? 'today' : `in the next ${dueDateFilter} days`}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Top Performing Agencies */}
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Top Performing Customers</h3>
            <div className="mt-5">
              <div className="space-y-4">
                {agencyCompletionRates.slice(0, 5).map((agency) => (
                  <div
                    key={`${agency.name}-${agency.totalTasks}`}
                    className="flex items-center"
                  >
                    <div className="w-32 text-sm text-gray-500 dark:text-gray-400 truncate">{agency.name}</div>
                    <div className="flex-1">
                      <div className="relative h-4 bg-gray-100 dark:bg-gray-700 rounded">
                        <div
                          className="absolute h-4 bg-indigo-600 rounded transition-all duration-300" 
                          style={{ width: `${agency.completionRate || 0}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-20 text-right text-sm text-gray-500 dark:text-gray-400">
                      <span title={`${agency.completedTasks} of ${agency.totalTasks} tasks completed`}>
                        {agency.completionRate !== null ? `${agency.completionRate}%` : '-'}
                      </span>
                    </div>
                  </div>
                ))}
                {agencyCompletionRates.length === 0 && (
                  <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                    No task completion data available yet
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}